import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { getCategory, getPost, getPostSeo, getRelatedPosts, getThumbnail } from "@/data/blog";
import { getPostImage } from "@/data/blogImages";
import { ArrowLeft, ArrowRight, Calendar, Clock, Sparkles } from "lucide-react";

export const Route = createFileRoute("/blog/$category/$slug")({
  head: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) {
      return { meta: [{ title: "Article not found — legaldecoder" }] };
    }
    const seo = getPostSeo(post);
    const cat = getCategory(post.category);
    const siteOrigin = seo.canonicalUrl.split("/blog/")[0];
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { name: "keywords", content: seo.keyword },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: seo.canonicalUrl },
        { property: "og:image", content: seo.ogImage },
        { property: "article:published_time", content: post.publishedAt },
        { property: "article:modified_time", content: post.updatedAt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: seo.title },
        { name: "twitter:description", content: seo.description },
        { name: "twitter:image", content: seo.ogImage },
      ],
      links: [{ rel: "canonical", href: seo.canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: seo.description,
            image: seo.ogImage,
            datePublished: post.publishedAt,
            dateModified: post.updatedAt,
            author: { "@type": "Organization", name: "legaldecoder" },
            publisher: {
              "@type": "Organization",
              name: "legaldecoder",
              logo: { "@type": "ImageObject", url: "https://www.legaldecoder.io/logo.png" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": seo.canonicalUrl },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${siteOrigin}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${siteOrigin}/blog` },
              ...(cat
                ? [
                    {
                      "@type": "ListItem",
                      position: 3,
                      name: cat.name,
                      item: `${siteOrigin}/blog/${post.category}`,
                    },
                  ]
                : []),
              {
                "@type": "ListItem",
                position: cat ? 4 : 3,
                name: post.title,
                item: seo.canonicalUrl,
              },
            ],
          }),
        },
      ],
    };
  },
  component: PostPage,
  notFoundComponent: NotFound,
});

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-primary-glow hover:underline">
          Back to blog
        </Link>
      </div>
    </div>
  );
}

function PostPage() {
  const { category, slug } = Route.useParams();
  const post = getPost(slug);
  const cat = getCategory(category);

  if (!post || !cat) return <NotFound />;

  const related = getRelatedPosts(slug, 3);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <article>
          {/* Hero */}
          <section className="relative overflow-hidden border-b border-border">
            <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
            <div className="relative mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
              <Link
                to="/blog/$category"
                params={{ category }}
                className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> {cat.name}
              </Link>
              <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
                {post.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  Updated{" "}
                  {new Date(post.updatedAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {post.readTime} read
                </span>
              </div>
            </div>
          </section>

          {/* Featured image */}
          <section className="border-b border-border bg-muted/30">
            <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
              <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                <img
                  src={getPostImage(post.slug)}
                  alt={`${cat.name} — ${post.title}`}
                  width={1024}
                  height={576}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Content */}
          <section className="py-12">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
              {/* TL;DR */}
              <div className="mb-10 rounded-2xl border-2 border-primary-glow/30 bg-primary-glow/5 p-6">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary-glow">
                  <Sparkles className="h-4 w-4" /> TL;DR
                </div>
                <p className="text-base leading-relaxed text-foreground">{post.tldr}</p>
              </div>

              {/* Sections */}
              <div className="space-y-8">
                {post.sections.map((s) => (
                  <section key={s.heading}>
                    <h2 className="text-2xl font-bold tracking-tight">{s.heading}</h2>
                    <p className="mt-3 text-base leading-relaxed text-muted-foreground">{s.body}</p>
                  </section>
                ))}
              </div>

              {/* Inline CTA */}
              <div className="my-12 rounded-2xl border border-border bg-gradient-hero p-8 text-center shadow-elegant">
                <h3 className="text-xl font-bold text-primary-foreground sm:text-2xl">
                  Have a lease in front of you right now?
                </h3>
                <p className="mx-auto mt-2 max-w-md text-primary-foreground/80">
                  Upload it. Our AI flags every red flag mentioned in this guide — in under 10
                  seconds.
                </p>
                <Button variant="hero" size="lg" className="mt-5 gap-2" asChild>
                  <Link to="/">
                    Decode My Lease <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              {/* FAQ */}
              {post.faq.length > 0 && (
                <section className="mt-12">
                  <h2 className="text-2xl font-bold tracking-tight">Frequently asked questions</h2>
                  <div className="mt-6 space-y-4">
                    {post.faq.map((f) => (
                      <div key={f.q} className="rounded-xl border border-border bg-card p-5">
                        <h3 className="font-semibold">{f.q}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </section>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="border-t border-border bg-surface py-16">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <h2 className="mb-6 text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Related guides
              </h2>
              <div className="grid gap-6 md:grid-cols-3">
                {related.map(({ post: p, reason }) => {
                  const pCat = getCategory(p.category);
                  const thumb = getThumbnail(p);
                  return (
                    <Link
                      key={p.slug}
                      to="/blog/$category/$slug"
                      params={{ category: p.category, slug: p.slug }}
                      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:-translate-y-1 hover:shadow-card-hover"
                    >
                      <div
                        className={`relative flex aspect-[16/9] items-center justify-center bg-gradient-to-br ${thumb.gradient}`}
                        aria-hidden
                      >
                        <span className="text-5xl drop-shadow-sm">{thumb.icon}</span>
                        {pCat && (
                          <span className="absolute left-3 top-3 rounded-full bg-background/80 px-2.5 py-1 text-[11px] font-semibold text-foreground backdrop-blur">
                            {pCat.name}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <h3 className="text-lg font-semibold leading-snug group-hover:text-primary-glow">
                          {p.title}
                        </h3>
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                          {p.excerpt}
                        </p>
                        <div className="mt-4 flex items-start gap-2 rounded-lg border border-primary-glow/20 bg-primary-glow/5 px-3 py-2">
                          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-glow" />
                          <p className="text-xs leading-snug text-foreground/80">
                            <span className="font-semibold text-primary-glow">Why related:</span>{" "}
                            {reason}
                          </p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </main>
    </div>
  );
}
